export const ADVERTISING_CONSENT_VERSION = '2026-09-20' as const
export const ADVERTISING_CONSENT_COOKIE = 'cg_advertising_consent'
export const ADVERTISING_CONSENT_CHANGED_EVENT = 'cg:advertising-consent-changed'
export const ADVERTISING_MEASUREMENT_ACTIVATE_FUNCTION =
  'cgActivateAdvertisingMeasurement'
export const ADVERTISING_CONSENT_PARTYTOWN_FORWARD = [
  'dataLayer.push',
  ADVERTISING_MEASUREMENT_ACTIVATE_FUNCTION,
] as const

export type AdvertisingConsentSignal = 'granted' | 'denied'
export type AdvertisingConsentBasis = 'express' | 'implied'

export type AdvertisingConsentSnapshot = {
  version: typeof ADVERTISING_CONSENT_VERSION
  /** Absent only on legacy snapshots created by the former explicit banner. */
  basis?: AdvertisingConsentBasis
  decidedAt: string
  analyticsStorage: AdvertisingConsentSignal
  adStorage: AdvertisingConsentSignal
  adUserData: AdvertisingConsentSignal
  adPersonalization: AdvertisingConsentSignal
}

export type ConsentModeSignals = {
  analytics_storage: AdvertisingConsentSignal
  ad_storage: AdvertisingConsentSignal
  ad_user_data: AdvertisingConsentSignal
  ad_personalization: AdvertisingConsentSignal
}

type ConsentDataLayer = {
  push(...items: unknown[]): unknown
}

type MicrosoftUetConsentQueue = {
  push(
    event: 'consent',
    command: 'default' | 'update',
    signals: { ad_storage: AdvertisingConsentSignal },
  ): unknown
}

export const DEFAULT_DENIED_CONSENT: AdvertisingConsentSnapshot = {
  version: ADVERTISING_CONSENT_VERSION,
  decidedAt: '',
  analyticsStorage: 'denied',
  adStorage: 'denied',
  adUserData: 'denied',
  adPersonalization: 'denied',
}

const COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60
const RFC3339_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/

function isRfc3339(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    RFC3339_PATTERN.test(value) &&
    !Number.isNaN(Date.parse(value))
  )
}

export function isAdvertisingConsentSnapshot(
  value: unknown,
): value is AdvertisingConsentSnapshot {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  if (candidate.version !== ADVERTISING_CONSENT_VERSION) return false
  if (!isRfc3339(candidate.decidedAt)) return false
  const signals = [
    candidate.analyticsStorage,
    candidate.adStorage,
    candidate.adUserData,
    candidate.adPersonalization,
  ]
  if (candidate.basis !== undefined && candidate.basis !== 'express' && candidate.basis !== 'implied') {
    return false
  }
  if (!signals.every((signal) => signal === 'granted' || signal === 'denied')) return false
  return signals.every((signal) => signal === signals[0])
}

function projectAdvertisingConsent(
  value: unknown,
): AdvertisingConsentSnapshot | undefined {
  if (!isAdvertisingConsentSnapshot(value)) return undefined
  return {
    version: value.version,
    ...(value.basis ? { basis: value.basis } : {}),
    decidedAt: value.decidedAt,
    analyticsStorage: value.analyticsStorage,
    adStorage: value.adStorage,
    adUserData: value.adUserData,
    adPersonalization: value.adPersonalization,
  }
}

export function createAdvertisingConsentSnapshot(
  choice: AdvertisingConsentSignal,
  decidedAt = new Date().toISOString(),
  basis: AdvertisingConsentBasis = 'express',
): AdvertisingConsentSnapshot {
  return {
    version: ADVERTISING_CONSENT_VERSION,
    basis,
    decidedAt,
    analyticsStorage: choice,
    adStorage: choice,
    adUserData: choice,
    adPersonalization: choice,
  }
}

export function createImpliedAdvertisingConsentSnapshot(
  decidedAt = new Date().toISOString(),
): AdvertisingConsentSnapshot {
  return createAdvertisingConsentSnapshot('granted', decidedAt, 'implied')
}

function cookieValue(cookieHeader: string): string | undefined {
  const prefix = `${ADVERTISING_CONSENT_COOKIE}=`
  for (const part of cookieHeader.split(';')) {
    const trimmed = part.trim()
    if (trimmed.startsWith(prefix)) return trimmed.slice(prefix.length)
  }
  return undefined
}

export function readExplicitAdvertisingConsent(
  cookieHeader: string,
): AdvertisingConsentSnapshot | undefined {
  const raw = cookieValue(cookieHeader)
  if (!raw) return undefined
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as unknown
    return projectAdvertisingConsent(parsed)
  } catch {
    return undefined
  }
}

export function readAdvertisingConsent(cookieHeader: string): AdvertisingConsentSnapshot {
  return readExplicitAdvertisingConsent(cookieHeader) ?? DEFAULT_DENIED_CONSENT
}

export function isAdvertisingMeasurementGranted(cookieHeader: string): boolean {
  const consent = readExplicitAdvertisingConsent(cookieHeader)
  return (
    consent?.analyticsStorage === 'granted' &&
    consent.adStorage === 'granted' &&
    consent.adUserData === 'granted' &&
    consent.adPersonalization === 'granted'
  )
}

export function isBrowserAdvertisingMeasurementGranted(): boolean {
  return (
    typeof window !== 'undefined' &&
    isAdvertisingMeasurementGranted(window.document.cookie)
  )
}

export function isExpressAdvertisingConsentGranted(value: unknown): boolean {
  return (
    isAdvertisingConsentSnapshot(value) &&
    value.basis !== 'implied' &&
    value.analyticsStorage === 'granted'
  )
}

/**
 * Canada permits an opt-out model for non-sensitive advertising cookies when
 * notice and a durable, immediate opt-out remain available. This creates a
 * truthful implied-basis record instead of pretending the visitor clicked an
 * explicit banner.
 */
export function ensureBrowserAdvertisingConsent(): AdvertisingConsentSnapshot | undefined {
  if (typeof document === 'undefined') return undefined
  const persistedValue = cookieValue(document.cookie)
  const existing = readExplicitAdvertisingConsent(document.cookie)
  if (existing) return existing
  // A present-but-invalid cookie may be a future-version or legacy denial.
  // Never replace an unrecognized persisted choice with an implied grant.
  if (persistedValue !== undefined) return DEFAULT_DENIED_CONSENT
  const snapshot = createImpliedAdvertisingConsentSnapshot()
  document.cookie = serializeAdvertisingConsentCookie(snapshot)
  notifyAdvertisingConsentChanged(snapshot)
  return snapshot
}

export function setBrowserAdvertisingConsent(
  choice: AdvertisingConsentSignal,
): AdvertisingConsentSnapshot | undefined {
  if (typeof document === 'undefined') return undefined
  const snapshot = createAdvertisingConsentSnapshot(choice)
  document.cookie = serializeAdvertisingConsentCookie(snapshot)
  notifyAdvertisingConsentChanged(snapshot)
  return snapshot
}

export function notifyAdvertisingConsentChanged(
  snapshot: AdvertisingConsentSnapshot,
): void {
  if (typeof window === 'undefined') return
  const dataLayer = (window as Window & { dataLayer?: ConsentDataLayer }).dataLayer
  if (dataLayer) queueConsentModeCommand(dataLayer, 'update', toConsentModeSignals(snapshot))
  const microsoftQueue = (window as Window & { uetq?: MicrosoftUetConsentQueue }).uetq
  if (microsoftQueue && typeof microsoftQueue.push === 'function') {
    try {
      queueMicrosoftUetConsentCommand(microsoftQueue, 'update', snapshot)
    } catch {
      // Measurement consent is best-effort and must never break the privacy control.
    }
  }
  window.dispatchEvent(
    new CustomEvent(ADVERTISING_CONSENT_CHANGED_EVENT, { detail: snapshot }),
  )
}

export function toIntakeConsent(
  snapshot: AdvertisingConsentSnapshot | undefined,
): AdvertisingConsentSnapshot | undefined {
  return projectAdvertisingConsent(snapshot)
}

export function toConsentModeSignals(snapshot: AdvertisingConsentSnapshot): ConsentModeSignals {
  return {
    analytics_storage: snapshot.analyticsStorage,
    ad_storage: snapshot.adStorage,
    ad_user_data: snapshot.adUserData,
    ad_personalization: snapshot.adPersonalization,
  }
}

export function serializeAdvertisingConsentCookie(snapshot: AdvertisingConsentSnapshot): string {
  return `${ADVERTISING_CONSENT_COOKIE}=${encodeURIComponent(JSON.stringify(snapshot))}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax; Secure`
}

/** Queue the same Arguments object produced by Google's supported gtag snippet. */
export function queueConsentModeCommand(
  dataLayer: ConsentDataLayer,
  command: 'default' | 'update',
  signals: ConsentModeSignals,
): void {
  function gtag(...command: ['consent', 'default' | 'update', ConsentModeSignals]) {
    void command
    // Google's supported representation is the function's Arguments object.
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments)
  }
  gtag('consent', command, signals)
}

/** Queue Microsoft's supported UET Consent Mode command. */
export function queueMicrosoftUetConsentCommand(
  queue: MicrosoftUetConsentQueue,
  command: 'default' | 'update',
  snapshot: AdvertisingConsentSnapshot,
): void {
  queue.push('consent', command, { ad_storage: snapshot.adStorage })
}

/**
 * Runs in GTM's execution context immediately before its bootstrap. The
 * explicit-cookie update is deliberately signal-only: no cookie value, click
 * identifier, or URL is copied into dataLayer.
 */
export function advertisingConsentBootstrapScript(): string {
  const denied = JSON.stringify(toConsentModeSignals(DEFAULT_DENIED_CONSENT))
  const cookieName = JSON.stringify(ADVERTISING_CONSENT_COOKIE)
  const version = JSON.stringify(ADVERTISING_CONSENT_VERSION)
  const decidedAtPattern = JSON.stringify(RFC3339_PATTERN.source)
  return `(function(w,d){w.dataLayer=w.dataLayer||[];w.gtag=w.gtag||function(){w.dataLayer.push(arguments)};var denied=${denied};w.gtag('consent','default',denied);try{var prefix=${cookieName}+'=',raw=d.cookie.split(';').map(function(v){return v.trim()}).find(function(v){return v.indexOf(prefix)===0});if(!raw)return;var c=JSON.parse(decodeURIComponent(raw.slice(prefix.length))),signals=[c.analyticsStorage,c.adStorage,c.adUserData,c.adPersonalization];if(c.version!==${version}||typeof c.decidedAt!=='string'||!(new RegExp(${decidedAtPattern})).test(c.decidedAt)||isNaN(Date.parse(c.decidedAt))||!signals.every(function(v){return v==='granted'||v==='denied'})||!signals.every(function(v){return v===signals[0]}))return;w.gtag('consent','update',{analytics_storage:c.analyticsStorage,ad_storage:c.adStorage,ad_user_data:c.adUserData,ad_personalization:c.adPersonalization})}catch(e){}})(window,document);`
}

export function advertisingConsentGtmBootstrapScript(gtmId: string): string {
  return `${advertisingConsentBootstrapScript()}(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`
}

/**
 * Install the one-shot GTM activation function inside Partytown's execution
 * context. It validates the consent cookie itself because the worker has a
 * separate global object from the main thread. The client calls the forwarded
 * function after its shared validation pass; the immediate call covers a
 * persisted grant and Partytown's main-thread fallback.
 */
export function advertisingMeasurementGtmActivationScript(gtmId: string): string {
  const functionName = JSON.stringify(ADVERTISING_MEASUREMENT_ACTIVATE_FUNCTION)
  const cookieName = JSON.stringify(ADVERTISING_CONSENT_COOKIE)
  const version = JSON.stringify(ADVERTISING_CONSENT_VERSION)
  const decidedAtPattern = JSON.stringify(RFC3339_PATTERN.source)
  const bootstrap = advertisingConsentGtmBootstrapScript(gtmId)
  return `(function(w,d){var activate=function(){if(w.__cgAdvertisingGtmActivated)return;try{var prefix=${cookieName}+'=',raw=d.cookie.split(';').map(function(v){return v.trim()}).find(function(v){return v.indexOf(prefix)===0});if(!raw)return;var c=JSON.parse(decodeURIComponent(raw.slice(prefix.length))),signals=[c.analyticsStorage,c.adStorage,c.adUserData,c.adPersonalization];if(c.version!==${version}||typeof c.decidedAt!=='string'||!(new RegExp(${decidedAtPattern})).test(c.decidedAt)||isNaN(Date.parse(c.decidedAt))||!signals.every(function(v){return v==='granted'}))return;w.__cgAdvertisingGtmActivated=true;${bootstrap}}catch(e){}};w[${functionName}]=activate;activate()})(window,document);`
}
